import { Link } from 'react-router-dom'
import { useCloudStorageStatus } from '../../hooks/useCloudStorageStatus'

/**
 * Top-of-page banner shown when the workspace has Drive-stored documents
 * but no active Drive connection — those files are visible in the list
 * but cannot be viewed or downloaded until the owner reconnects.
 *
 * Renders nothing in the healthy state. Designed to be dropped near the
 * top of any document-listing or document-detail page.
 *
 * Props:
 *   className    — extra Tailwind classes for the wrapper
 *   showOnAnyConnectionIssue — also surface for status='revoked' even
 *                              when provider_file_count is 0 (default false)
 */
const StrandedFilesBanner = ({ className = '', showOnAnyConnectionIssue = false } = {}) => {
  const { loading, status, isStranded, providerLabel, strandedCount } = useCloudStorageStatus()

  if (loading) return null

  // Standard case: stranded files detected
  const showForStrandedCount = isStranded

  // Optional: also show on revoked/disconnected when no docs were affected (rare,
  // but useful for first-time disconnect awareness)
  const showForBareDisconnect = showOnAnyConnectionIssue
    && status
    && !status.connected
    && status.provider
    && status.status === 'revoked'

  if (!showForStrandedCount && !showForBareDisconnect) return null

  const isRevoked = status?.status === 'revoked'
  const tone = isRevoked ? 'red' : 'amber'
  const palette = tone === 'red'
    ? {
        bg: 'bg-danger-soft',
        border: 'border-red-300',
        text: 'text-red-900',
        button: 'bg-red-600 hover:bg-red-700 text-white',
        icon: '⚠',
      }
    : {
        bg: 'bg-warning-soft',
        border: 'border-amber-300',
        text: 'text-amber-900',
        button: 'bg-amber-600 hover:bg-amber-700 text-white',
        icon: '⚠',
      }

  const title = isRevoked
    ? `${providerLabel} access was revoked`
    : `${providerLabel} is not connected`

  const message = strandedCount > 0
    ? <>
        <strong>{strandedCount}</strong> file{strandedCount === 1 ? '' : 's'} stored on {providerLabel}{' '}
        {strandedCount === 1 ? 'is' : 'are'} not accessible inside OVACS right now.
        Reconnect to view and download {strandedCount === 1 ? 'it' : 'them'}.
      </>
    : <>
        New uploads in this workspace will go to OVACS local storage until you reconnect {providerLabel}.
      </>

  return (
    <div
      className={`rounded-lg border ${palette.bg} ${palette.border} ${palette.text} p-4 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${className}`}
      role="alert"
    >
      <div className="flex items-start gap-3 flex-1">
        <span className="text-xl leading-none mt-0.5" aria-hidden="true">{palette.icon}</span>
        <div className="text-sm">
          <div className="font-semibold mb-0.5">{title}</div>
          <div className="opacity-90">{message}</div>
        </div>
      </div>
      <Link
        to="/profile-settings?tab=cloud-storage"
        className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded ${palette.button} flex-shrink-0`}
      >
        Reconnect {providerLabel}
      </Link>
    </div>
  )
}

export default StrandedFilesBanner
